import { useEffect, useState } from "react";

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen">
      <section id="home" className="relative min-h-screen flex flex-col">
        {/* Navigation */}
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled ? "bg-white shadow-md" : "bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection("home")}>
                <img
                  src="https://public.readdy.ai/ai/img_res/0e861264-75ff-4390-8eda-552b537fd94f.png"
                  alt="VeroTax Services Logo"
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-xl font-bold text-gray-800">VeroTax Services</h1>
                  <p className="text-xs text-gray-600">VeroTax Group LLC</p>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <button
                  onClick={() => scrollToSection("home")}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-200"
                  }`}
                >
                  Inicio
                </button>
                <button
                  onClick={() => scrollToSection("services")}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-200"
                  }`}
                >
                  Servicios
                </button>
                <button
                  onClick={() => scrollToSection("why-choose-us")}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-200"
                  }`}
                >
                  Por Qué Elegirnos
                </button>
                <button
                  onClick={() => scrollToSection("about")}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-200"
                  }`}
                >
                  Nosotros
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isScrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-200"
                  }`}
                >
                  Contacto
                </button>
                <button
                  onClick={() => scrollToSection("schedule")}
                  className="px-5 py-2 bg-teal-600 text-white text-sm font-medium rounded-md hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Agendar Cita
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative flex-1 flex items-center justify-center">
          <img
            src="https://public.readdy.ai/ai/img_res/7a927fdca8901ba8892960c1028d7a81.jpg"
            alt="Hero Background"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 text-center w-full">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Servicios Profesionales de Impuestos, ITIN y Notaría en Maryland
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Brindamos apoyo a la comunidad hispana en preparación de impuestos, trámites de ITIN, licencias de conducir para inmigrantes, servicios notariales remotos y apostillas. Atención profesional con claridad y responsabilidad.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => scrollToSection("schedule")}
                className="px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-md hover:bg-teal-700 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
              >
                Agendar una Cita
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="px-8 py-4 bg-white text-gray-800 text-base font-semibold rounded-md hover:bg-gray-100 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
              >
                Contáctanos
              </button>
              <button
                onClick={() => setShowVideo(true)}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-base font-semibold rounded-md hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/30"
              >
                <i className="ri-play-circle-line mr-2"></i>
                Ver Video
              </button>
            </div>
          </div>
        </div>

        {/* Video Modal */}
        {showVideo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setShowVideo(false)}>
            <div className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white rounded-full text-gray-800 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="aspect-video">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                  title="VeroTax Services Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Professional Highlights Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-2">+10 años</div>
              <div className="text-sm text-gray-600">Experiencia profesional</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-xl font-bold text-teal-600 mb-2">Atención personalizada</div>
              <div className="text-sm text-gray-600">Cada cliente importa</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-xl font-bold text-teal-600 mb-2">Servicio bilingüe</div>
              <div className="text-sm text-gray-600">Español e inglés</div>
            </div>
            <div className="text-center">
              <div className="text-lg md:text-xl font-bold text-teal-600 mb-2">Con cita previa</div>
              <div className="text-sm text-gray-600">Horario flexible</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
